import { useEffect, useState, useCallback } from "react";
import { api, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Loader2, X } from "lucide-react";

const PERIODS = [
  { v: "week", l: "This Week" }, { v: "month", l: "This Month" },
  { v: "quarter", l: "This Quarter" }, { v: "year", l: "This Year" }, { v: "all", l: "All Time" },
];
const RANKS = [
  { v: "defects", l: "Defects reported" }, { v: "sales", l: "Sales value" }, { v: "conversion", l: "Conversion rate" },
];

function range(period) {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const iso = (d) => d.toISOString().slice(0, 10);
  if (period === "week") { const day = (now.getDay() + 6) % 7; const s = new Date(now); s.setDate(now.getDate() - day); return [iso(s), iso(now)]; }
  if (period === "month") return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)];
  if (period === "quarter") { const q = Math.floor(now.getMonth() / 3); return [iso(new Date(now.getFullYear(), q * 3, 1)), iso(now)]; }
  if (period === "year") return [iso(new Date(now.getFullYear(), 0, 1)), iso(now)];
  return [null, null];
}

const MEDALS = ["text-yellow-500", "text-slate-400", "text-amber-700"];

export default function DefectLeaderboard({ currency = "EUR" }) {
  const [period, setPeriod] = useState("year");
  const [rank, setRank] = useState("defects");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);

  const custom = !!(from || to);
  const load = useCallback(() => {
    const [f, t] = custom ? [from || null, to || null] : range(period);
    const params = { rank };
    if (f) params.from_date = f;
    if (t) params.to_date = t;
    api.get("/defects/leaderboard", { params }).then((r) => setData(r.data)).catch(() => setData({ rows: [] }));
  }, [period, rank, from, to, custom]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const rankLabel = RANKS.find((r) => r.v === rank)?.l;

  return (
    <section className="bg-white border border-slate-200 rounded-md p-4" data-testid="defect-leaderboard">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="font-head font-bold text-slate-900 flex items-center gap-2"><Trophy className="h-5 w-5 text-yellow-500" />Defect Leaderboard</h2>
          <p className="text-xs text-slate-500">Defects reported by staff and the quote &amp; sales value they generate · ranked by <b>{rankLabel}</b></p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-md px-2 h-9">
            <input type="date" data-testid="leaderboard-from" value={from} onChange={(e) => setFrom(e.target.value)} className="text-xs text-slate-700 bg-transparent outline-none w-[110px]" />
            <span className="text-slate-300">–</span>
            <input type="date" data-testid="leaderboard-to" value={to} onChange={(e) => setTo(e.target.value)} className="text-xs text-slate-700 bg-transparent outline-none w-[110px]" />
            {custom && <button data-testid="leaderboard-clear-range" onClick={() => { setFrom(""); setTo(""); }} className="text-slate-400 hover:text-slate-700"><X className="h-3.5 w-3.5" /></button>}
          </div>
          <Select value={period} onValueChange={setPeriod} disabled={custom}>
            <SelectTrigger data-testid="leaderboard-period" className="w-[130px] bg-white h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={rank} onValueChange={setRank}>
            <SelectTrigger data-testid="leaderboard-rank" className="w-[160px] bg-white h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{RANKS.map((r) => <SelectItem key={r.v} value={r.v}>Rank: {r.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-x-auto scroll-thin">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50">{["#", "Employee", "Defects Reported", "Quotes Raised", "Quote Value", "Converted to Sales", "Sales Value", "Conversion Rate"].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2 whitespace-nowrap text-xs uppercase tracking-wide">{h}</th>)}</tr></thead>
          <tbody>
            {data === null && <tr><td colSpan={8} className="text-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {data && rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">No defects recorded by staff in this period.</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.name} data-testid={`leaderboard-row-${i}`} className="border-t border-slate-100">
                <td className="px-3 py-2.5 w-10">
                  {i < 3 ? <Trophy className={`h-4 w-4 ${MEDALS[i]}`} /> : <span className="text-slate-400 text-xs">{i + 1}</span>}
                </td>
                <td className="px-3 py-2.5 font-medium text-slate-800">{r.name}</td>
                <td className="px-3 py-2.5 text-slate-700">{r.defects}</td>
                <td className="px-3 py-2.5 text-slate-700">{r.quote_count}</td>
                <td className="px-3 py-2.5 text-slate-700">{money(r.quote_value, currency)}</td>
                <td className="px-3 py-2.5 text-slate-700">{r.converted_count}</td>
                <td className="px-3 py-2.5 font-semibold text-emerald-700">{money(r.converted_value, currency)}</td>
                <td className="px-3 py-2.5">
                  <span className={`font-semibold ${r.conversion_rate >= 50 ? "text-emerald-600" : r.conversion_rate > 0 ? "text-amber-600" : "text-slate-400"}`}>{r.conversion_rate}%</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Only defects formally recorded and attributed to an employee are counted. Quote/sales figures come from quotations raised against those defects.</p>
    </section>
  );
}
